import axios from 'axios';

/**
 * Urbanride Booking API Service
 *
 * This service provides a wrapper around the Urbanride API for booking
 * chauffeured transportation services. All API calls are proxied through
 * the backend to avoid CORS issues and keep API keys secure.
 */

// Track configuration state
let apiConfigured = false;
let apiMode = 'test';
let autoProvisionAvailable = false;

/**
 * Initialize or update the Urbanride API client with the user's configuration
 * @returns {Promise<{configured: boolean, mode: string, autoProvisionAvailable: boolean}>}
 */
export async function initializeUrbanrideApi() {
  try {
    // Fetch the configuration status from the backend
    const response = await axios.get('/users/me/urbanride-api-key');
    const { mode, configured, auto_provision_available } = response.data;

    apiConfigured = configured;
    apiMode = mode;
    autoProvisionAvailable = auto_provision_available || false;

    return { configured, mode, autoProvisionAvailable };
  } catch (error) {
    console.error('Failed to initialize Urbanride API:', error);
    apiConfigured = false;
    autoProvisionAvailable = false;
    return { configured: false, mode: 'unknown', autoProvisionAvailable: false };
  }
}

/**
 * Provision an Urbanride API key for the current user.
 * This is called when a user first tries to use the booking service
 * and doesn't have an API key configured.
 * @returns {Promise<{status: string, mode: string, message: string}>}
 */
export async function provisionApiKey() {
  try {
    const response = await axios.post('/urbanride/provision-api-key');
    const result = response.data;

    // If provisioning was successful, reinitialize to update the state
    if (result.status === 'provisioned' || result.status === 'already_configured') {
      await initializeUrbanrideApi();
    }

    return result;
  } catch (error) {
    console.error('Failed to provision Urbanride API key:', error);
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to provision API key';
    throw new Error(errorMessage);
  }
}

/**
 * Check if auto-provisioning is available for the current user
 * @returns {boolean}
 */
export function isAutoProvisionAvailable() {
  return autoProvisionAvailable;
}

/**
 * Check if the Urbanride API is configured and ready
 */
export function isConfigured() {
  return apiConfigured;
}

/**
 * Get current configuration status
 */
export function getConfigStatus() {
  return {
    configured: apiConfigured,
    mode: apiMode
  };
}

// ============================================
// Location Search Functions
// ============================================

/**
 * @typedef {Object} LocationQuery
 * @property {string} input - Search string (airport code, address, etc.)
 * @property {string} [type] - Optional filter (airport, train-station, address, fbo)
 * @property {string} [airport] - Optional airport code filter when type='fbo' (e.g., "TEB")
 */

/**
 * @typedef {Object} LocationDTO
 * @property {string} type - Category of location
 * @property {string} id - Unique identifier
 * @property {string} name - Human-readable name
 * @property {string} details - Full postal address
 */

/**
 * Search for locations (airports, addresses, train stations, FBOs)
 * @param {LocationQuery} locationQuery
 * @returns {Promise<{locations: LocationDTO[]}>}
 */
export async function searchLocations(locationQuery) {
  // Note: Removed apiConfigured check - let the backend handle auth/config errors
  // The booking flow works, so the API should be configured
  const resp = await axios.post('/urbanride/location/search', locationQuery);
  return resp.data;
}

/**
 * Search for airlines
 * @param {{input: string}} query
 * @returns {Promise<{airlines: Array<{code: string, name: string}>}>}
 */
export async function searchAirlines(query) {
  if (!apiConfigured) {
    throw new Error('Urbanride API not configured');
  }

  const resp = await axios.post('/urbanride/airline/search', query);
  return resp.data;
}

/**
 * Search for passengers in the Urbanride system
 * @param {{input: string}} query
 * @returns {Promise<{passengers: Array}>}
 */
export async function searchPassengers(query) {
  if (!apiConfigured) {
    throw new Error('Urbanride API not configured');
  }

  const resp = await axios.post('/urbanride/pax/search', query);
  return resp.data;
}

/**
 * Search for railroads by name or code
 * @param {{input: string}} query - Search query with input string
 * @returns {Promise<{railroads: Array<{code: string, name: string}>}>}
 */
export async function searchRailroads(query) {
  if (!apiConfigured) {
    throw new Error('Urbanride API not configured');
  }

  const resp = await axios.post('/urbanride/railroad/search', query);
  return resp.data;
}

// ============================================
// Flight Validation
// ============================================

/**
 * @typedef {Object} FlightQuery
 * @property {string} airport - Airport IATA code (3 chars)
 * @property {string} airline - Airline IATA code (2 chars)
 * @property {string} flightNumber - Flight number
 * @property {string} dateTime - "yyyy-MM-dd'T'HH:mm"
 * @property {boolean} departure - false for arrival, true for departure
 */

/**
 * Validate a flight and get its details
 * @param {FlightQuery} flightQuery
 * @returns {Promise<Object>} Flight details including status, terminals, gates, etc.
 */
export async function validateFlight(flightQuery) {
  if (!apiConfigured) {
    throw new Error('Urbanride API not configured');
  }

  const resp = await axios.post('/urbanride/flight/validate', flightQuery);
  return resp.data;
}

// ============================================
// Rates and Quotes
// ============================================

/**
 * @typedef {Object} BookingAirport
 * @property {string} dateTime - "yyyy-MM-dd'T'HH:mm"
 * @property {string} comments - Comments
 * @property {string} airport - Airport IATA code
 * @property {string} airline - Airline IATA code
 * @property {string} flightNumber - Flight number
 * @property {string} [otherAirport] - Other airport code (origin for pickup, destination for dropoff)
 */

/**
 * @typedef {Object} BookingAddress
 * @property {string} dateTime - "yyyy-MM-dd'T'HH:mm"
 * @property {string} comments - Comments
 * @property {string} id - ID from search/address
 * @property {string} placeId - Google PlaceID
 */

/**
 * @typedef {Object} BookingLocation
 * @property {string} type - "airport", "train-station", "address", "fbo"
 * @property {BookingAirport} [airport] - Airport details if type is "airport"
 * @property {BookingAddress} [address] - Address details if type is "address"
 */

/**
 * @typedef {Object} BookingPax
 * @property {string} [title] - "", "Mr.", "Mrs.", "Dr.", "Prof.", "Ms.", "Miss", "Sir"
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} mobile - Mobile phone with international code
 * @property {string} email
 * @property {boolean} [vip]
 * @property {number} [extra] - Number of additional guests
 * @property {Array<string>} [extraNames] - Names of additional guests
 */

/**
 * @typedef {Object} BookingCmd
 * @property {BookingLocation} pickup
 * @property {BookingLocation} dropoff
 * @property {Array<BookingLocation>} stops
 * @property {Array<BookingPax>} passengers
 * @property {string} [clientComments]
 * @property {string} [vehicleId] - Vehicle ID from rate
 * @property {string} [confirmationId] - Confirmation ID from rate
 * @property {boolean} asQuote - true for QUOTE workflow, false for BEGIN
 */

/**
 * @typedef {Object} RateResponse
 * @property {string} rate - Rate with currency
 * @property {string} rateExt - Extended rate with currency
 * @property {number} [minHours] - Min hours for hourly rate
 * @property {string} note - Notes
 * @property {string} vehicle - Vehicle name
 * @property {string} vehicleImageUrl - Vehicle image URL
 * @property {number} capacity - Vehicle capacity
 * @property {number} luggage - Vehicle luggage capacity
 * @property {string} cancellationPolicy - Cancellation policy
 * @property {Array<string>} surcharges - Additional surcharges
 * @property {number} vehicleId - Vehicle ID for booking
 * @property {string} confirmationId - Confirmation ID for booking
 */

/**
 * Get rates for a booking
 * @param {BookingCmd} bookingCmd
 * @returns {Promise<{rates: RateResponse[]}>}
 */
export async function getRates(bookingCmd) {
  if (!apiConfigured) {
    throw new Error('Urbanride API not configured');
  }

  const resp = await axios.post('/urbanride/rate/get', bookingCmd);
  return resp.data;
}

/**
 * Get quotes for a booking
 * @param {BookingCmd} bookingCmd
 * @returns {Promise<{rates: RateResponse[]}>}
 */
export async function getQuotes(bookingCmd) {
  if (!apiConfigured) {
    throw new Error('Urbanride API not configured');
  }

  const resp = await axios.post('/urbanride/quote/get', bookingCmd);
  return resp.data;
}

// ============================================
// Booking
// ============================================

/**
 * Book a trip
 * @param {BookingCmd} bookingCmd - Include vehicleId and confirmationId from rate
 * @returns {Promise<{urcn: string}>} - Unique reference number for the booking
 */
export async function bookTrip(bookingCmd) {
  if (!apiConfigured) {
    throw new Error('Urbanride API not configured');
  }

  const resp = await axios.post('/urbanride/book', bookingCmd);
  return resp.data;
}

// ============================================
// Trip Management
// ============================================

/**
 * @typedef {Object} TripDetailsResponse
 * @property {string} urcn - Urbanride confirmation number
 * @property {string} status - Trip status (e.g., "confirmed", "cancelled", "completed")
 * @property {string} pickupDateTime - Pickup date/time
 * @property {Object} pickup - Pickup location details
 * @property {Object} dropoff - Dropoff location details
 * @property {Array<Object>} stops - Array of intermediate stops
 * @property {Array<Object>} passengers - Array of passengers
 * @property {Object} vehicle - Vehicle details
 */

/**
 * Get trip details by URCN (V2 API format)
 * @param {string} urcn - Urbanride confirmation number
 * @returns {Promise<TripDetailsResponse>} Trip details
 */
export async function getTripDetails(urcn) {
  if (!apiConfigured) {
    throw new Error('Urbanride API not configured');
  }

  const resp = await axios.post('/urbanride/trip/get', { urcn });
  return resp.data;
}

/**
 * @typedef {Object} ActiveTripsQuery
 * @property {string} [from] - Start date filter (yyyy-MM-dd)
 * @property {string} [to] - End date filter (yyyy-MM-dd)
 * @property {string} [status] - Status filter (e.g., "active", "confirmed")
 */

/**
 * Search for active trips
 * @param {ActiveTripsQuery} query - Search query
 * @returns {Promise<{trips: TripDetailsResponse[]}>} Search results
 */
export async function searchActiveTrips(query = {}) {
  if (!apiConfigured) {
    throw new Error('Urbanride API not configured');
  }

  const resp = await axios.post('/urbanride/trip/search', query);
  return resp.data;
}

/**
 * @typedef {Object} CancelTripResponse
 * @property {string} urcn - Urbanride confirmation number
 * @property {string} status - New status (should be "cancelled")
 * @property {string} [message] - Optional message about cancellation
 */

/**
 * Update an existing trip reservation by URCN
 * @param {string} urcn - Urbanride confirmation number to update
 * @param {BookingCmd} trip - Updated booking details
 * @returns {Promise<{urcn: string}>} Update result
 */
export async function updateTrip(urcn, trip) {
  if (!apiConfigured) {
    throw new Error('Urbanride API not configured');
  }

  const resp = await axios.post('/urbanride/trip/update', { urcn, trip });
  return resp.data;
}

/**
 * Cancel a trip by URCN (V2 API format)
 * Note: This function does not check apiConfigured because cancellation should
 * be attempted regardless - the backend will validate the API key and return
 * an appropriate error if not configured.
 * @param {string} urcn - Urbanride confirmation number to cancel
 * @returns {Promise<CancelTripResponse>} Cancellation result
 */
export async function cancelTrip(urcn) {
  const resp = await axios.post('/urbanride/trip/cancel', { urcn });
  return resp.data;
}

// ============================================
// Constants
// ============================================

export const LOCATION_TYPES = ['airport', 'train-station', 'address', 'fbo', 'as-directed', ''];

export const TITLES = ['', 'Mr.', 'Mrs.', 'Dr.', 'Prof.', 'Ms.', 'Miss', 'Sir'];

// ============================================
// Google Distance Matrix API
// ============================================

/**
 * @typedef {Object} DistanceMatrixRequest
 * @property {string} [originPlaceId] - Google Place ID for origin
 * @property {string} [originAddress] - Address string for origin (fallback if no placeId)
 * @property {string} [destinationPlaceId] - Google Place ID for destination
 * @property {string} [destinationAddress] - Address string for destination (fallback if no placeId)
 * @property {string} [departureTime] - ISO datetime for traffic-aware routing
 */

/**
 * @typedef {Object} DistanceMatrixResponse
 * @property {boolean} success - Whether the request was successful
 * @property {string} status - API status
 * @property {number} [durationSeconds] - Travel duration in seconds
 * @property {string} [durationText] - Human-readable duration (e.g., "38 mins")
 * @property {number} [distanceMeters] - Distance in meters
 * @property {string} [distanceText] - Human-readable distance (e.g., "25.3 km")
 * @property {string} [originAddress] - Resolved origin address
 * @property {string} [destinationAddress] - Resolved destination address
 * @property {string} [error] - Error message if unsuccessful
 */

/**
 * Get travel duration between two locations using Google Distance Matrix API.
 * This is used to calculate suggested pickup times for hotel pickups when
 * the destination has a specific start time (e.g., meetings).
 *
 * @param {DistanceMatrixRequest} request
 * @returns {Promise<DistanceMatrixResponse>}
 */
export async function getDistanceMatrix(request) {
  try {
    const resp = await axios.post('/google-places/distance-matrix', {
      origin_place_id: request.originPlaceId || null,
      origin_address: request.originAddress || null,
      destination_place_id: request.destinationPlaceId || null,
      destination_address: request.destinationAddress || null,
      departure_time: request.departureTime || null
    });

    const data = resp.data;

    if (data.success) {
      return {
        success: true,
        status: data.status,
        durationSeconds: data.duration_seconds,
        durationText: data.duration_text,
        distanceMeters: data.distance_meters,
        distanceText: data.distance_text,
        originAddress: data.origin_address,
        destinationAddress: data.destination_address
      };
    } else {
      return {
        success: false,
        status: data.status || 'ERROR',
        error: data.error || 'Unknown error'
      };
    }
  } catch (error) {
    console.error('Distance Matrix API error:', error);
    return {
      success: false,
      status: 'EXCEPTION',
      error: error.message || 'Failed to calculate travel time'
    };
  }
}

/**
 * Round up a duration in seconds to the nearest 15-minute interval.
 * For example: 38 minutes (2280 seconds) rounds up to 45 minutes (2700 seconds).
 *
 * @param {number} durationSeconds - Duration in seconds
 * @returns {number} Rounded duration in seconds
 */
export function roundUpToNearest15Minutes(durationSeconds) {
  const fifteenMinutesInSeconds = 15 * 60; // 900 seconds
  return Math.ceil(durationSeconds / fifteenMinutesInSeconds) * fifteenMinutesInSeconds;
}

/**
 * Calculate suggested pickup time based on destination start time and travel duration.
 * Rounds up travel time to nearest 15 minutes and subtracts from destination time.
 *
 * @param {Date|string} destinationStartTime - When the passenger needs to arrive
 * @param {number} travelDurationSeconds - Estimated travel time in seconds
 * @returns {Date} Suggested pickup time
 */
export function calculateSuggestedPickupTime(destinationStartTime, travelDurationSeconds) {
  const destTime = new Date(destinationStartTime);
  const roundedDuration = roundUpToNearest15Minutes(travelDurationSeconds);

  // Subtract the rounded travel time from destination time
  return new Date(destTime.getTime() - (roundedDuration * 1000));
}

// ============================================
// Utility Functions
// ============================================

/**
 * Format date to "yyyy-MM-dd'T'HH:mm" format
 * @param {Date} date
 * @returns {string}
 */
export function formatDateTime(date) {
  const pad = n => `${n}`.padStart(2, '0');
  return (
    date.getFullYear() +
    '-' + pad(date.getMonth() + 1) +
    '-' + pad(date.getDate()) +
    'T' + pad(date.getHours()) +
    ':' + pad(date.getMinutes())
  );
}

/**
 * Parse a datetime string to Date object
 * @param {string} dateTimeStr - Format: "yyyy-MM-dd'T'HH:mm"
 * @returns {Date}
 */
export function parseDateTime(dateTimeStr) {
  return new Date(dateTimeStr);
}

/**
 * Create a booking location for an airport pickup/dropoff
 * @param {Object} params
 * @param {string} params.airport - Airport IATA code
 * @param {string} params.airline - Airline IATA code
 * @param {string} params.flightNumber - Flight number
 * @param {Date|string} params.dateTime - Date and time
 * @param {boolean} [params.departure] - true for departure, false for arrival
 * @param {string} [params.comments] - Comments
 * @param {Object} [params.flight] - Validated flight data from validateFlight API
 * @returns {BookingLocation}
 */
export function createAirportLocation({ airport, airline, flightNumber, dateTime, departure = false, comments = '', flight = null }) {
  const dt = typeof dateTime === 'string' ? dateTime : formatDateTime(dateTime);
  const location = {
    type: 'airport',
    id: airport,
    airport: {
      airport,
      airline,
      flightNumber,
      departure,
      dateTime: dt,
      comments
    }
  };
  if (flight) {
    location.flight = flight;
  }
  return location;
}

/**
 * Create a booking location for an address pickup/dropoff
 * @param {Object} params
 * @param {string} params.id - Location ID from search
 * @param {string} params.placeId - Google Place ID
 * @param {Date|string} params.dateTime - Date and time
 * @param {string} [params.comments] - Comments
 * @returns {BookingLocation}
 */
export function createAddressLocation({ id, placeId, dateTime, comments = '' }) {
  const dt = typeof dateTime === 'string' ? dateTime : formatDateTime(dateTime);
  return {
    type: 'address',
    address: {
      id,
      placeId,
      dateTime: dt,
      comments
    }
  };
}

/**
 * Create a booking location for an FBO (Fixed Base Operator) - private aviation terminal
 * Per API spec, BookingFbo has: dateTime, comments, id, airline, flightNumber, tailNumber
 * @param {Object} params
 * @param {string} params.id - FBO ID (from FBO search)
 * @param {Date|string} params.dateTime - Pickup/dropoff time
 * @param {string} [params.tailNumber] - Aircraft tail number (optional)
 * @param {string} [params.airline] - Airline IATA code (optional, for charter flights)
 * @param {string} [params.flightNumber] - Flight number (optional)
 * @param {string} [params.comments] - Additional comments
 * @returns {BookingLocation}
 */
export function createFboLocation({ id, dateTime, tailNumber = '', airline = '', flightNumber = '', comments = '' }) {
  const dt = typeof dateTime === 'string' ? dateTime : formatDateTime(dateTime);
  return {
    type: 'fbo',
    fbo: {
      id,
      dateTime: dt,
      tailNumber,
      airline,
      flightNumber,
      comments
    }
  };
}

/**
 * Create a booking location for an "as-directed" stop or dropoff (chauffeur service)
 * The passenger will direct the driver to the destination.
 * @param {Object} params
 * @param {Date|string} params.dateTime - Date and time
 * @param {string} [params.comments] - Custom comments (defaults to stop message)
 * @returns {BookingLocation}
 */
export function createAsDirectedLocation({ dateTime, comments }) {
  const dt = typeof dateTime === 'string' ? dateTime : formatDateTime(dateTime);
  return {
    type: 'as-directed',
    address: {
      id: null,
      placeId: null,
      dateTime: dt,
      comments: comments || 'Passenger/s will direct to one or more stops.'
    }
  };
}

/**
 * Create a booking location for a train station pickup or dropoff
 * @param {Object} params
 * @param {string} params.station - Train station code (e.g., "NYP" for NY Penn Station)
 * @param {Date|string} params.dateTime - Date and time
 * @param {string} [params.railroad] - Railroad code (e.g., "AM" for Amtrak)
 * @param {string} [params.trainNumber] - Train number
 * @param {string} [params.comments] - Comments
 * @returns {BookingLocation}
 */
export function createTrainStationLocation({ station, dateTime, railroad = '', trainNumber = '', comments = '' }) {
  const dt = typeof dateTime === 'string' ? dateTime : formatDateTime(dateTime);
  return {
    type: 'train-station',
    trainStation: {
      station: station,
      railroad: railroad,
      trainNumber: trainNumber,
      dateTime: dt,
      comments: comments
    }
  };
}

/**
 * Create a passenger object
 * @param {Object} params
 * @param {number} [params.id] - Urbanride pax ID (if provided, reuses existing profile)
 * @param {string} params.firstName
 * @param {string} params.lastName
 * @param {string} params.mobile
 * @param {string} params.email
 * @param {string} [params.title]
 * @param {boolean} [params.vip]
 * @param {number} [params.extra] - Additional guests
 * @param {string[]} [params.extraNames] - Names of additional guests
 * @returns {BookingPax}
 */
export function createPassenger({ id, firstName, lastName, mobile, email, title = '', vip = false, extra = 0, extraNames = [] }) {
  const passenger = {
    title,
    firstName,
    lastName,
    mobile,
    email,
    vip,
    extra,
    extraNames
  };

  // Include the Urbanride pax ID if provided to reuse existing profile
  if (id) {
    passenger.id = id;
  }

  return passenger;
}

export default {
  initializeUrbanrideApi,
  isConfigured,
  getConfigStatus,
  provisionApiKey,
  isAutoProvisionAvailable,
  searchLocations,
  searchAirlines,
  searchPassengers,
  searchRailroads,
  validateFlight,
  getRates,
  getQuotes,
  bookTrip,
  getTripDetails,
  searchActiveTrips,
  updateTrip,
  cancelTrip,
  getDistanceMatrix,
  roundUpToNearest15Minutes,
  calculateSuggestedPickupTime,
  formatDateTime,
  parseDateTime,
  createAirportLocation,
  createAddressLocation,
  createFboLocation,
  createAsDirectedLocation,
  createTrainStationLocation,
  createPassenger,
  LOCATION_TYPES,
  TITLES
};
